import { markdownToHtml } from 'framework/markdownEditor';
import { formatDate } from 'domain/shared/newVacancy';
import { getOutcomeActiveForText } from 'domain/projects/newProject/businessOutcomes/getOutcomeActiveFor';
import {
  CHANGE_REQUEST_STATUS_OPTIONS,
  MILESTONE_STATUS_OPTIONS,
  PROJECT_PROBABILITY_OPTIONS,
  RISK_STATUS_OPTIONS,
} from 'domain/shared/newProject';

const sanitizeCellText = (value: unknown): string => String(value ?? '').replace(/\r\n|\r|\n/g, '<br>');

const sortMilestonesByInitialPlannedDate = (milestones: any[]): any[] =>
  [...milestones].sort((a, b) => {
    const aDate = a?.initialPlannedDate || '';
    const bDate = b?.initialPlannedDate || '';
    if (!aDate) return bDate ? 1 : 0;
    if (!bDate) return -1;
    return aDate.localeCompare(bDate);
  });

export const generateOldViewMilestonesTable = (milestones: any[]): string => {
  if (!milestones || milestones.length === 0) {
    return 'No milestones available';
  }

  const header =
    '| ID | Milestone | Originally planned date | Currently planned date | Actual completion date | Comment |';
  const separator = '|---------|-----------|-------------------------|----------------------|----------|---------|';

  const rows = sortMilestonesByInitialPlannedDate(milestones).map(milestone => {
    const id = milestone.alias || '';
    const title = milestone.title || '';
    const initialPlannedDate = milestone.initialPlannedDate ? formatDate(milestone.initialPlannedDate) : '';
    const currentPlannedDate = milestone.currentPlannedDate ? formatDate(milestone.currentPlannedDate) : '';
    const completionDate = milestone.completionDate ? formatDate(milestone.completionDate) : '';
    const comment = milestone.comment || '';

    return `| ${id} | ${title} | ${initialPlannedDate} | ${currentPlannedDate} | ${completionDate} | ${comment} |`;
  });

  return [header, separator, ...rows].join('\n');
};

export const generateMilestonesTable = (milestones: any[]): string => {
  if (!milestones || milestones.length === 0) {
    return 'No milestones available';
  }

  const cellStyle = 'word-break: break-word;';

  let table = `<table>
    <thead>
      <tr>
        <th>ID</th>
        <th>Milestone</th>
        <th>Status</th>
        <th>Originally planned date</th>
        <th>Currently planned date</th>
        <th>Actual completion date</th>
      </tr>
    </thead>
    <tbody>`;

  sortMilestonesByInitialPlannedDate(milestones).forEach(milestone => {
    const id = milestone.alias || '';
    const title = sanitizeCellText(milestone.title);
    const status = MILESTONE_STATUS_OPTIONS[milestone.status] || '';
    const initialPlannedDate = milestone.initialPlannedDate ? formatDate(milestone.initialPlannedDate) : '';
    const currentPlannedDate = milestone.currentPlannedDate ? formatDate(milestone.currentPlannedDate) : '';
    const completionDate = milestone.completionDate ? formatDate(milestone.completionDate) : '';

    const description = sanitizeCellText(milestone?.description);
    const comment = sanitizeCellText(milestone?.comment);
    const rejectionReason = sanitizeCellText(milestone?.rejectionReason);

    const details: string[] = [];
    if (description) details.push(`<strong>Description:</strong> ${description}`);
    if (comment) details.push(`<strong>Currently planned date reason:</strong> ${comment}`);
    if (rejectionReason) details.push(`<strong>Rejection reason:</strong> ${rejectionReason}`);

    const hasDetails = details.length > 0;

    table += `
    <tr>
      <td${hasDetails ? ' rowspan="2"' : ''}>${id}</td>
      <td style="${cellStyle}">${title}</td>
      <td>${status}</td>
      <td style="${cellStyle}">${initialPlannedDate}</td>
      <td style="${cellStyle}">${currentPlannedDate}</td>
      <td style="${cellStyle}">${completionDate}</td>
    </tr>`;

    if (hasDetails) {
      table += `
    <tr>
      <td colspan="5" style="${cellStyle}">${details.join('<br>')}</td>
    </tr>`;
    }
  });

  table += `
    </tbody>
  </table>`;

  return table;
};

export const generateBusinessOutcomesTable = (businessOutcomes: any[]): string => {
  if (!businessOutcomes || businessOutcomes.length === 0) {
    return 'No outcomes available';
  }

  const header = '| ID | Business outcome |  Status | Active For | Related milestones |';
  const separator = '|---------|-----------|---------------|---------------|------|';

  const rows = businessOutcomes.map(outcome => {
    const id = outcome.alias || '';
    const title = sanitizeCellText(outcome.name);
    const status = MILESTONE_STATUS_OPTIONS[outcome.status] || '';
    const activeFor = getOutcomeActiveForText(outcome) || '';
    const relatedMilestones = outcome.milestones?.join(', ') || '';

    return `| ${id} | ${title} |  ${status} | ${activeFor} | ${relatedMilestones} |`;
  });

  return [header, separator, ...rows].join('\n');
};

export const generateCRTable = (businessOutcomes: any[]): string => {
  if (!businessOutcomes || businessOutcomes.length === 0) {
    return 'No change requests available';
  }

  const header = '| ID | Change request | Creation date | Status |';
  const separator = '|---------|-----------|-------------------------|---------------|';

  const rows = businessOutcomes.map(cr => {
    const id = cr.alias || '';
    const title = cr.title || '';
    const createdAt = formatDate(cr.createdAt) || '';
    const status = CHANGE_REQUEST_STATUS_OPTIONS[cr.status] || '';

    return `|  ${id}  |  ${title}  |  ${createdAt}  | ${status} |`;
  });

  return [header, separator, ...rows].join('\n');
};

export const generateRiskTable = (risks: any[]): string => {
  if (!risks || risks.length === 0) {
    return 'No risks available';
  }

  const cellStyle = 'word-break: break-word;';
  const equalColumnStyle = 'width: 15%; text-align: center;';
  const ownerCellStyle = 'word-break: normal; overflow-wrap: normal; white-space: normal; text-align: center;';

  let table = `<table>
    <tr>
      <th style="text-align: center">ID</th>
      <th style="text-align: center">Risk</th>
      <th style="${equalColumnStyle}">Probability</th>
      <th style="${equalColumnStyle}">Severity</th>
      <th style="${equalColumnStyle}">Status</th>
      <th style="${equalColumnStyle}">Owner</th>
    </tr>`;

  risks.forEach(risk => {
    const id = risk.alias || '';
    const title = risk.title || '';
    const probability = PROJECT_PROBABILITY_OPTIONS[risk.probability] || '';
    const severity = PROJECT_PROBABILITY_OPTIONS[risk.severity] || '';
    const status = RISK_STATUS_OPTIONS[risk.status] || '';
    const owner = risk.riskOwnerName || risk.riskOwner || '';
    const description = risk?.description ? markdownToHtml(risk.description).replace(/[\r\n]+/g, ``) : '';
    const resolvedAt = risk?.resolvedAt || '';
    const mitigationStrategy = risk.mitigationStrategy
      ? markdownToHtml(risk.mitigationStrategy).replace(/[\r\n]+/g, ``)
      : '';
    const detailsRowsCount = [description, mitigationStrategy, resolvedAt].filter(Boolean).length;
    const idRowSpan = detailsRowsCount > 0 ? `rowspan="${detailsRowsCount + 1}"` : '';

    table += `
    <tr>
      <td style="text-align: center" ${idRowSpan}>${id}</td>
      <td style="${cellStyle}">${title}</td>
      <td  style="${equalColumnStyle}">${probability}</td>
      <td  style="${equalColumnStyle}">${severity}</td>
      <td  style="${equalColumnStyle}">${status}</td>
      <td style="${ownerCellStyle}">${owner}</td>
    </tr>`;

    if (description) {
      table += `
    <tr>
      <td colspan="5" style="${cellStyle}"><strong>Details:</strong> ${description}</td>
    </tr>`;
    }
    if (mitigationStrategy) {
      table += `
    <tr>
      <td colspan="5" style="${cellStyle}"><strong>Mitigation Strategy:</strong> ${mitigationStrategy}</td>
    </tr>`;
    }
    if (resolvedAt) {
      table += `
    <tr>
      <td colspan="5" style="${cellStyle}"><strong>Resolved date:</strong> ${resolvedAt}</td>
    </tr>`;
    }
  });

  table += `</table>`;
  return table;
};
